import { FC, useCallback, useEffect, useState } from 'react'
import { Box, Button, Flex, SimpleGrid, VStack, Text } from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import { Choice } from '../../state/interfaces'
import { DefaultCheckbox } from './default-checkbox'

export interface DefaultCheckboxesProps {
  choices: Array<Choice>
  onChange?: (newValues: string[]) => void
  onConfirm?: (values: string[]) => void
}

export const DefaultCheckboxes: FC<DefaultCheckboxesProps> = ({ choices, onConfirm }) => {
  const { t } = useTranslation()

  const [selectedChoices, setSelectedChoices] = useState<string[]>([])
  const [formattedSelectedChoices, setFormattedSelectedChoices] = useState<string[]>([])

  const handleCheckboxChange = (value: string, checked: boolean) => {
    if (checked) {
      setSelectedChoices((prevSelected) => [...prevSelected, value])
    } else {
      setSelectedChoices((prevSelected) => prevSelected.filter((val) => val !== value))
    }
  }

  useEffect(() => {
    rearrangeSelectedChoices(selectedChoices)
  }, [selectedChoices])

  useEffect(() => {
    choices.forEach((choice) => {
      if (choice.isChecked) {
        setSelectedChoices((prevSelected) => [...prevSelected, choice.value])
      }
    })
  }, [])

  const confirmChoices = useCallback(() => {
    onConfirm && onConfirm(formattedSelectedChoices)
  }, [onConfirm, formattedSelectedChoices])

  const rearrangeSelectedChoices = (selectedChoice: string[]) => {
    const bluePrintSelectedChoices = [
      '3G',
      '4G',
      '5G',
      'unlimitedtext',
      'calldisplay',
      'voicemail',
      'eveningweekend',
      'national',
      'canadausa',
    ]

    const currentChoices = bluePrintSelectedChoices.filter((choice) => selectedChoice.includes(choice))
    setFormattedSelectedChoices(currentChoices)
  }

  return (
    <VStack>
      <Flex direction="column" marginTop={5}>
        <Text my={4}>{t('common_technologies')}:</Text>
        <Flex justifyContent="left" mb={5}>
          <SimpleGrid columns={3} spacingX={5} spacingY={4} alignItems="start">
            {choices
              .filter((choice) => choice.group === 'technology')
              .map((choice) => (
                <Box key={choice.value}>
                  <DefaultCheckbox
                    key={choice.value}
                    choice={choice}
                    isChecked={choice.isChecked}
                    onChange={handleCheckboxChange}
                  />
                </Box>
              ))}
          </SimpleGrid>
        </Flex>
        <Text my={4}>{t('common_options')}:</Text>
        <Flex justifyContent="center" mb={5}>
          <SimpleGrid columns={3} spacingX={5} spacingY={4} alignItems="start">
            {choices
              .filter((choice) => choice.group === 'options')
              .map((choice) => (
                <Box key={choice.value}>
                  <DefaultCheckbox
                    key={choice.value}
                    choice={choice}
                    isChecked={choice.isChecked}
                    onChange={handleCheckboxChange}
                  />
                </Box>
              ))}
          </SimpleGrid>
        </Flex>
      </Flex>
      <Button
        onClick={confirmChoices}
        disabled={selectedChoices.length === 0}
        variant="solid"
        color="white"
        backgroundColor="green.600"
        fontSize="xs"
        fontWeight="bold"
        borderWidth="2px"
        borderColor="green.600"
        px={16}
        py={6}
        _focus={{
          borderColor: 'brand',
          boxShadow: '0 0 0 2px #5bce4c',
        }}
      >
        {t('common_continue')}
      </Button>
    </VStack>
  )
}
